import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { format as formatDate, addDays } from "date-fns";

const {
  FiX,
  FiSave,
  FiUsers,
  FiCalendar,
  FiMapPin,
  FiClock,
  FiFileText,
  FiInfo,
  FiList,FiZap, FiLoader
} = FiIcons;

// --- START: TimeSelectionModal Component ---
const TimeSelectionModal = ({ isOpen, onClose, onSelectTime, currentTime }) => {
  const timeSlots = [
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
  ];

  const handleSelect = (time) => {
    onSelectTime(time);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            className="bg-white rounded-xl shadow-2xl w-full max-w-xs flex flex-col"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">เลือกเวลา</h3>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-slate-100"
              >
                <FiX className="text-slate-500" />
              </button>
            </div>
            <div className="p-2 grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
              {timeSlots.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleSelect(time)}
                  className={`w-full text-center py-2.5 rounded-lg font-semibold transition-colors ${
                    currentTime === time
                      ? "bg-green-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
// --- END: TimeSelectionModal Component ---

// --- START: TimeInput Component (พิมพ์เองได้ + เปิด Modal) ---
const TimeInput = ({ label, name, value, onChange, onOpenModal }) => {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={name}
        className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
      >
        <SafeIcon icon={FiClock} className="w-4 h-4 text-slate-400" /> {label}
      </label>
      <div className="relative">
        <input
          type="time"
          id={name}
          name={name}
          value={value || ""}
          onChange={onChange}
          className="w-full p-2.5 pr-10 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 font-semibold text-slate-700"
        />
        <button
          type="button"
          onClick={onOpenModal}
          className="absolute top-1/2 right-2 -translate-y-1/2 p-1 text-slate-500 hover:text-green-600 hover:bg-slate-100 rounded-md"
          title="เลือกเวลาจากรายการ"
        >
          <FiList className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
// --- END: TimeInput Component ---

const MeetingDetailsForm = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  onAIAssist,
}) => {
  const [formData, setFormData] = useState({});
  const [editingTimeField, setEditingTimeField] = useState(null);
  const [isAILoading, setIsAILoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: "",
        date: formatDate(new Date(), "yyyy-MM-dd"),
        startTime: "09:00",
        endTime: "10:00",
        location: "",
        participants: "",
        agenda: "",
        notes: "",
        ...initialData,
      });
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateShortcut = (daysToAdd) => {
    const newDate = addDays(new Date(), daysToAdd);
    setFormData((prev) => ({
      ...prev,
      date: formatDate(newDate, "yyyy-MM-dd"),
    }));
  };

  const handleTimeSelect = (time) => {
    if (editingTimeField) {
      setFormData((prev) => ({ ...prev, [editingTimeField]: time }));
    }
    setEditingTimeField(null);
  };

  const handleAIAssistClick = async () => {
    if (!onAIAssist) return;
    setIsAILoading(true);
    try {
      await onAIAssist();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการเรียก AI: " + error.message);
    } finally {
      setIsAILoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-slate-50 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-shrink-0 gap-4">
              <h2 className="text-xl font-bold text-green-800">
                รายละเอียดการประชุม
              </h2>
              <div className="flex items-center gap-2">
                {/* --- ปุ่ม "สร้างด้วย AI" --- */}
                <button
                  type="button"
                  onClick={handleAIAssistClick}
                  disabled={isAILoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isAILoading ? (
                    <>
                      <FiLoader className="w-4 h-4 animate-spin" />
                      <span>กำลังคิด...</span>
                    </>
                  ) : (
                    <>
                      <FiZap className="w-4 h-4" />
                      <span>สร้างด้วย AI</span>
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full text-slate-500 hover:text-green-600 hover:bg-green-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-5 space-y-5 overflow-y-auto"
            >
              {/* Title */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="title"
                  className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                >
                  <SafeIcon
                    icon={FiFileText}
                    className="w-4 h-4 text-slate-400"
                  />{" "}
                  หัวข้อการประชุม <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title || ""}
                  onChange={handleChange}
                  className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  placeholder="เช่น สรุปยอดขาย Q3"
                  required
                />
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="date"
                    className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                  >
                    <SafeIcon
                      icon={FiCalendar}
                      className="w-4 h-4 text-slate-400"
                    />{" "}
                    วันที่
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date || ""}
                    onChange={handleChange}
                    className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  />
                  <div className="flex items-center gap-1 mt-1">
                    <button
                      type="button"
                      onClick={() => handleDateShortcut(0)}
                      className="text-xs px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full"
                    >
                      วันนี้
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateShortcut(-1)}
                      className="text-xs px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full"
                    >
                      เมื่อวาน
                    </button>
                  </div>
                </div>
                <TimeInput
                  label="เวลาเริ่มต้น"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  onOpenModal={() => setEditingTimeField("startTime")}
                />
                <TimeInput
                  label="เวลาสิ้นสุด"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  onOpenModal={() => setEditingTimeField("endTime")}
                />
              </div>

              {/* --- START: ส่วนของข้อมูลอื่นๆ ทั้งหมด --- */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Location */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="location"
                    className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                  >
                    <SafeIcon
                      icon={FiMapPin}
                      className="w-4 h-4 text-slate-400"
                    />{" "}
                    สถานที่
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location || ""}
                    onChange={handleChange}
                    className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                    placeholder="เช่น ออนไลน์, ห้องประชุมชั้น 5"
                  />
                </div>
                {/* Participants */}
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="participants"
                    className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                  >
                    <SafeIcon
                      icon={FiUsers}
                      className="w-4 h-4 text-slate-400"
                    />{" "}
                    ผู้เข้าร่วม
                  </label>
                  <input
                    type="text"
                    id="participants"
                    name="participants"
                    value={formData.participants || ""}
                    onChange={handleChange}
                    className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                    placeholder="คั่นด้วยจุลภาค (,)"
                  />
                </div>
              </div>

              {/* Agenda */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="agenda"
                  className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                >
                  <SafeIcon
                    icon={FiFileText}
                    className="w-4 h-4 text-slate-400"
                  />{" "}
                  วาระการประชุม
                </label>
                <textarea
                  id="agenda"
                  name="agenda"
                  value={formData.agenda || ""}
                  onChange={handleChange}
                  rows="3"
                  className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  placeholder="ระบุวาระการประชุมสั้นๆ..."
                ></textarea>
              </div>

              {/* Notes */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="notes"
                  className="text-sm font-medium text-slate-600 flex items-center gap-1.5"
                >
                  <SafeIcon icon={FiInfo} className="w-4 h-4 text-slate-400" />{" "}
                  หมายเหตุ
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  rows="2"
                  className="w-full p-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400"
                  placeholder="ข้อมูลเพิ่มเติมอื่นๆ (ถ้ามี)"
                ></textarea>
              </div>
              {/* --- END: ส่วนของข้อมูลอื่นๆ --- */}
            </form>

            <div className="p-4 border-t border-slate-200 flex-shrink-0 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-white text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-100 transition-colors font-semibold"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 font-semibold shadow-sm"
              >
                <SafeIcon icon={FiSave} className="w-5 h-5" /> บันทึก
              </button>
            </div>

            <TimeSelectionModal
              isOpen={editingTimeField !== null}
              onClose={() => setEditingTimeField(null)}
              onSelectTime={handleTimeSelect}
              currentTime={formData[editingTimeField]}
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MeetingDetailsForm;
